import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
// import { useUser } from '@clerk/clerk-react'; // This should be received as prop instead
import * as taskService from './services/taskService';
import BackendStatus from './components/BackendStatus';
import BackendOfflineNotice from './components/BackendOfflineNotice';

const PersonalBoardContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: white;
  padding: 20px 30px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #1f2937;
  margin: 0;
  font-size: 28px;
  font-weight: 700;
`;

const BackButton = styled.button`
  background: #6366f1;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover {
    background: #5045e6;
  }
`;

const Content = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-height: 500px;
`;

const TasksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const TaskCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #3b82f6;
  }
`;

const TaskTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
`;

const TaskDescription = styled.p`
  color: #6b7280;
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.5;
`;

const TaskMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
`;

const TaskBoard = styled.span`
  font-size: 12px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 12px;
`;

const TaskColumn = styled.span`
  font-size: 12px;
  color: #3b82f6;
  font-weight: 500;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6b7280;
  font-size: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyTitle = styled.h3`
  color: #374151;
  margin: 0 0 12px 0;
  font-size: 20px;
`;

const EmptyDescription = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fca5a5;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin: 16px 0;
`;

const PersonalBoard = ({ onBack, user }) => {
  // User should be passed as prop instead of using useUser hook
  const [tasks, setTasks] = useState([]);
  const [boards, setBoards] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storageMode, setStorageMode] = useState('api');

  useEffect(() => {
    if (user?.id) {
      loadPersonalTasks();
    }
  }, [user?.id]);

  const loadPersonalTasks = async () => {
    if (!user?.id) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get all boards first, then extract tasks assigned to this user
      let userTasks = [];
      
      try {
        // Get all boards
        const boards = await taskService.getBoards();
        console.log(`Personal Board: Found ${boards.length} boards`);
        
        // For each board, get its tasks and filter by assigned user
        for (const board of boards) {
          try {
            const boardTasks = await taskService.getTasks(board.id || board._id);
            console.log(`Personal Board: Board ${board.title} has ${boardTasks.length} tasks`);
            
            // Filter tasks assigned to this user
            const assignedTasks = boardTasks.filter(task => task.assignedUserId === user.id);
            console.log(`Personal Board: Found ${assignedTasks.length} tasks assigned to user ${user.id}`);
            
            // Add board information to each task
            const tasksWithBoard = assignedTasks.map(task => ({
              ...task,
              boardId: board.id || board._id,
              boardTitle: board.title
            }));
            
            userTasks.push(...tasksWithBoard);
          } catch (boardError) {
            console.warn(`Failed to get tasks for board ${board.id || board._id}:`, boardError);
          }
        }
        
        console.log(`Personal Board: Total assigned tasks found: ${userTasks.length}`);
        setStorageMode('api');
        
      } catch (backendError) {
        console.warn('Backend API failed, trying local storage:', backendError);
        
        // Fallback to local storage
        const allTasks = JSON.parse(localStorage.getItem('taskManager_tasks') || '[]');
        userTasks = allTasks.filter(task => task.assignedUserId === user.id);
        setStorageMode('local');
        setError('Backend unavailable. Showing local tasks only.');
      }

      setTasks(userTasks);

      // Load board information for each task
      const boardIds = [...new Set(userTasks.map(task => task.boardId).filter(Boolean))];
      const boardsData = {};

      for (const boardId of boardIds) {
        try {
          const board = await taskService.getBoard(boardId);
          if (board && board.title) {
            boardsData[boardId] = board;
          }
        } catch (boardError) {
          console.warn(`Failed to load board ${boardId}:`, boardError);
          boardsData[boardId] = { title: 'Unknown Board' };
        }
      }

      setBoards(boardsData);
      setStorageMode('api');
    } catch (err) {
      console.error('Error loading personal tasks:', err);
      setError('Failed to load your assigned tasks. Working in local mode.');
      setStorageMode('local');
      
      // Try to load from local storage as fallback
      try {
        const localTasks = JSON.parse(localStorage.getItem('taskManager_tasks') || '[]');
        const personalTasks = localTasks.filter(task => task.assignedUserId === user.id);
        setTasks(personalTasks);
      } catch (localError) {
        console.warn('Failed to load local tasks:', localError);
        setTasks([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = (task) => {
    // Navigate to the board containing this task
    if (task.boardId && onBack) {
      // You could implement navigation to the specific board here
      // For now, we'll just go back to the main view
      onBack();
    }
  };

  const getColumnDisplayName = (columnId) => {
    const columnNames = {
      'todo': 'To Do',
      'in-progress': 'In Progress',
      'inprogress': 'In Progress',
      'completed': 'Completed',
      'done': 'Done'
    };
    return columnNames[columnId] || columnId;
  };

  if (loading) {
    return (
      <PersonalBoardContainer>
        <BackendStatus />
        <Header>
          <Title>My Assigned Tasks</Title>
          <BackButton onClick={onBack}>Back to Boards</BackButton>
        </Header>
        <Content>
          <LoadingState>Loading your assigned tasks...</LoadingState>
        </Content>
      </PersonalBoardContainer>
    );
  }

  return (
    <PersonalBoardContainer>
      <BackendStatus />
      
      <Header>
        <Title>My Assigned Tasks</Title>
        <BackButton onClick={onBack}>Back to Boards</BackButton>
      </Header>

      <Content>
        {storageMode === 'local' && error && (
          <BackendOfflineNotice message={error} />
        )}

        {error && storageMode !== 'local' && (
          <ErrorMessage>{error}</ErrorMessage>
        )}

        {tasks.length === 0 ? (
          <EmptyState>
            <EmptyTitle>No Assigned Tasks</EmptyTitle>
            <EmptyDescription>
              You don't have any tasks assigned to you yet. 
              Tasks assigned to you will appear here.
            </EmptyDescription>
          </EmptyState>
        ) : (
          <TasksGrid>
            {tasks.map(task => (
              <TaskCard 
                key={task.id || task._id} 
                onClick={() => handleTaskClick(task)}
              >
                <TaskTitle>{task.title}</TaskTitle>
                {task.description && (
                  <TaskDescription>{task.description}</TaskDescription>
                )}
                <TaskMeta>
                  <TaskBoard>
                    {boards[task.boardId]?.title || 'Unknown Board'}
                  </TaskBoard>
                  <TaskColumn>
                    {getColumnDisplayName(task.columnId)}
                  </TaskColumn>
                </TaskMeta>
              </TaskCard>
            ))}
          </TasksGrid>
        )}
      </Content>
    </PersonalBoardContainer>
  );
};

export default PersonalBoard; 